import { useLanguage } from "@/contexts/LanguageContext";
import { Linkedin, Instagram } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalizedPath, stripLangPrefix } from "@/hooks/useLocalizedPath";
import logoImage from "@/assets/geonova-logo.png";

const Footer = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { localePath } = useLocalizedPath();

  const isHomePage = /^\/(fr|en)\/?$/.test(location.pathname);

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      navigate(localePath(""));
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const menuItems = [
    { label: t("nav.home"), id: "hero" },
    { label: t("nav.method"), id: "approach" },
    { label: t("nav.services"), id: "services" },
    { label: t("nav.cases"), id: "cases" },
    { label: t("nav.pricing"), id: "pricing" },
    { label: t("nav.faq"), id: "faq" },
    { label: t("nav.blog"), path: "/blog" },
    { label: t("nav.contact"), id: "contact" },
  ];

  return (
    <footer className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-100 via-slate-200 to-slate-300" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-slate-400/20 opacity-60" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="flex flex-col lg:flex-row items-center lg:items-center justify-between gap-8">
          <button
            onClick={() => scrollToSection("hero")}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <span className="text-base lg:text-lg font-display font-bold flex items-center">
              <span className="text-slate-700">G</span>
              <span className="relative inline-flex items-center justify-center h-7 lg:h-8 w-7 lg:w-8 -mx-0.5">
                <img 
                  src={logoImage} 
                  alt={language === "fr" ? "Gogenia.ai - Logo officiel de l'agence de référencement IA" : "Gogenia.ai - Official AI referencing agency logo"} 
                  className="h-full w-full object-contain filter brightness-0 invert-[.35] sepia-[.1] saturate-[0] hue-rotate-[0deg]"
                />
              </span>
              <span className="text-slate-700">GENIA</span>
              <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">.AI</span>
            </span>
          </button>

          <nav className="flex flex-wrap items-center justify-center gap-6 lg:gap-8">
            {menuItems.map((item) => (
              item.path ? (
                <a
                  key={item.label}
                  href={localePath(item.path)}
                  className="text-sm font-medium text-slate-500 hover:text-sky-500 transition-colors"
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.id!)}
                  className="text-sm font-medium text-slate-500 hover:text-sky-500 transition-colors"
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>
        </div>
      </div>

      <div className="border-t border-slate-300/80 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <div>
              {t("footer.copyright")}
            </div>
            <div className="flex items-center gap-4">
              <a 
                href="https://www.linkedin.com/company/gogenia/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-slate-500 hover:text-sky-500 transition-colors"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a 
                href="https://www.instagram.com/gogenia.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-slate-500 hover:text-pink-500 transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <span className="text-slate-300">•</span>
              <a href={localePath("/legal")} className="hover:text-sky-500 transition-colors">
                {t("footer.privacy")}
              </a>
              <span className="text-slate-300">•</span>
              <a href={localePath("/legal")} className="hover:text-sky-500 transition-colors">
                {t("footer.legal")}
              </a>
              <span className="text-slate-300">•</span>
              <a href="/admin" className="hover:text-sky-500 transition-colors">
                Admin
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
